import ExcelJS from 'exceljs'
import employee from '@/router/main/system/employee/employee'

// 导入参数数据类型
export interface importExcelType {
  // 第 i 张工作表
  i?: number
  // 表格表头字段数组
  header: readonly any[]
}

// 导出参数数据类型
export interface exportExcelType {
  // 数据
  data: { [key: string]: any }[]
  // 文件名
  name: string
  // 表头字段
  header: string[]
  // 表头字段对应中文
  customHeader: string[]
  // 工作表名
  sheetName?: string
  // 标题
  title?: string
  // 小标题
  subTitle?: string
  // 工作表保护密码
  password?: string
  // 对齐方式
  alignment?: Partial<ExcelJS.Alignment>
  // 合并单元格
  mergeList?: mergeListType[]
  // 标题样式
  titleStyle?: Partial<ExcelJS.Row>
  // 小标题样式
  subTitleStyle?: Partial<ExcelJS.Row>
  // 表头样式
  headerStyle?: Partial<ExcelJS.Row>
  // 单元格统一样式
  cellStyle?: Partial<ExcelJS.Row & ExcelJS.Column>
  // 颜色组
  colorConfigs?: CellColorConfig[],
  // 冻结列
  freezeColumn?: number
}

export interface CellColorConfig {
  rowIndex: number
  columnIndex: number
  color: string
}

// 合并单元格数据类型
export interface mergeListType {
  startRow: number
  startColumn: number
  endRow: number
  endColumn: number
}

// 更新导出参数数据类型，使其能描述单个工作表
export interface singleSheetExportType
  extends Omit<exportExcelType, 'sheetName'> {
  sheetName?: string // 每个工作表都需要明确指定名称
}

// exceljs相关方法
export class Excel {
  blob?: Blob // 导入的blob文件
  worksheet?: ExcelJS.Worksheet // 当前工作表
  header: string[] // 表头字段数组
  constructor(blob?: Blob) {
    this.blob = blob
    this.worksheet = undefined
    this.header = []
  }

  public async exportExcel(options: singleSheetExportType): Promise<void> {
    options.sheetName = options.sheetName || 'Sheet1'
    this.exportExcelMulti([options])
  }

  // 导出excel，参数信息参考exceljs
  // data 数据
  // name 文件名
  // header 表头字段
  // customHeader 表头字段对应中文
  // sheetName 工作表名
  // title 标题
  // subTitle 副标题（日期）
  // password 冻结表格密码
  // mergeList 合并单元格数组
  // titleStyle 标题样式(按需补充方法)
  // subTitleStyle 小标题样式(按需补充方法)
  // headerStyle 表头字段样式(按需补充方法)
  // cellStyle 单元格样式(按需补充方法)

  public async exportExcelMulti(
    sheetsConfig: singleSheetExportType[]
  ): Promise<void> {
    // 创建工作簿
    const workbook = new ExcelJS.Workbook()
    workbook.creator = '天鼎投资'
    workbook.created = new Date()

    sheetsConfig.forEach((config) => {
      const {
        data,
        name,
        header,
        customHeader,
        sheetName,
        title,
        subTitle,
        password,
        mergeList,
        titleStyle,
        subTitleStyle,
        headerStyle,
        cellStyle,
        colorConfigs,
        freezeColumn
      } = config

      // 添加sheet
      this.worksheet = workbook.addWorksheet(sheetName, {
        properties: { tabColor: { argb: 'FF00FF00' } }
      })

      // 重复现有的处理逻辑，但针对每个工作表
      this.header = header // 注意：这个设计可能需要调整，确保每个sheet的header独立处理
      // 省略具体实现细节，参照原方法内部逻辑，如设置标题、数据、样式等
      // 表头行序号
      let headerRowId = 1
      if (title) headerRowId++
      if (subTitle) headerRowId++
      // 插入单元格数据
      this.setCells(data, header, customHeader, cellStyle, colorConfigs)
      // 插入大标题
      this.getTitle(title, titleStyle)
      // 插入小标题
      this.getSubTitle(subTitle, subTitleStyle)
      // 处理表头
      this.setHeaderStyle(headerRowId, data, headerStyle)
      // 设置单元格颜色
      // this.setColorByConfig(colorConfigs)
      // 更多处理
      this.handleDealExcel(password, mergeList, headerRowId)

      if (freezeColumn) {
        this.worksheet.views = [{ state: 'frozen', xSplit: freezeColumn }]
      }
    })

    // 导出excel(此处也可用file-saver将blob导出到excel文件  fs.saveAs(blob, name+'.xlsx');)
    await workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = sheetsConfig[0].name + '.xlsx'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(a.href)
    })
  }

  // 合并单元格数组
  // merge1 需要合并的行/列起止对象数组1
  // 例：[{ startRow: 1, endRow: 1},{ startRow: 10, endRow: 10}]
  // merge2 需要合并的列/行起止对象数组2
  // 例：[{ startColumn: 1, endColumn: 2 }]
  // mergeArr 合并后数组
  // 例：[{ startRow: 1, endRow: 1, startColumn: 1, endColumn: 2},{ startRow: 10, endRow: 10, startColumn: 1, endColumn: 2}]
  public merge(
    merge1: Partial<mergeListType>[],
    merge2: Partial<mergeListType>[]
  ): mergeListType[] {
    const mergeArr: any[] = []
    merge1.forEach((item1) => {
      mergeArr.push(
        ...merge2.map((item2) => {
          return { ...item2, ...item1 }
        })
      )
    })
    return mergeArr
  }

  private setColorByConfig(configs: CellColorConfig[] | undefined): void {
    if (configs) {
      configs.forEach((config) => {
        const cell = this.worksheet?.getCell(
          `${config.columnIndex}${config.rowIndex}`
        )
        if (cell) {
          cell.font = {
            color: { argb: config.color }
          }
        }
      })
    }
  }

  // 单元格数据处理
  // data 表格数据
  // customHeader  表头中文字段

  private setCells(
    data: exportExcelType['data'],
    header: string[],
    customHeader: string[],
    style?: Partial<ExcelJS.Row & ExcelJS.Column>,
    configs?: CellColorConfig[]
  ): void {
    // 设置列，插入中文表头
    const column: Partial<ExcelJS.Column>[] = []
    this.header.forEach((item, index) => {
      column.push({
        header: customHeader[index],
        key: item,
        width: style?.width || 25
      })
    })
    this.worksheet!.columns = column
    // 设置行，添加数据
    this.worksheet?.addRows(data)
    // 设置行高
    this.worksheet?.eachRow({ includeEmpty: true }, (row: any, rowNumber) => {
      let maxHeight = 1
      row.values.forEach((cur: any) => {
        if (cur && typeof cur === 'string') {
          const tmp = cur.split('\r\n') // 多行数据以此来作为分割设置行高
          const tmpLen = tmp.length
          maxHeight = Math.max(tmpLen, maxHeight)
          maxHeight = Math.max(Math.ceil(cur.length / 60), maxHeight)
        }
      })
      row.height = style?.height || 20 * maxHeight
      console.log(configs)
      if (configs && configs.find((item) => item.rowIndex === rowNumber)) {
        row.eachCell((cell: any, colNumber: number) => {
          const cellStyle = configs.find(
            (item) =>
              item.rowIndex === rowNumber && item.columnIndex === colNumber
          )
          cell.font = style?.font || {
            color: { argb: cellStyle?.color }
          }
        })
      }
    })

    // const maxWidths = new Array(this.worksheet!.columns.length).fill(0)
    //
    // // 遍历所有行数据和列，计算最大宽度
    // data.forEach((row: any) => {
    //   if (row !== null && typeof row === 'object') {
    //     Object.keys(row).forEach((key) => {
    //       const cellWidth = String(row[key]).length
    //       maxWidths[header.indexOf(key)] = Math.max(
    //         maxWidths[header.indexOf(key)],
    //         cellWidth
    //       )
    //     })
    //   } else if (row !== null && Array.isArray(row)) {
    //     row.forEach((cellValue: any, columnIndex: number) => {
    //       const cellWidth = String(cellValue).length
    //       maxWidths[columnIndex] = Math.max(maxWidths[columnIndex], cellWidth)
    //     })
    //   }
    // })
    //
    // // 根据计算出的最大宽度设置列宽
    // maxWidths.forEach((width, index) => {
    //   // 可以根据需要调整一个系数，确保宽度足够
    //   const maxLength = 100
    //   const minLength = 10
    //   const adjustedWidth = Math.max(
    //     minLength,
    //     Math.min(maxLength, width * 1.2)
    //   )
    //   this.worksheet!.getColumn(index + 1).width = adjustedWidth // 注意列索引从1开始
    // })

    // 获取每一列数据，再依次对齐
    this.worksheet!.columns.forEach((column) => {
      column.alignment = style?.alignment || {
        vertical: 'middle',
        horizontal: 'center',
        wrapText: true
      }
    })
  }

  // 添加大标题
  // title 标题
  // style 标题样式
  private getTitle(
    title: string | undefined,
    style?: Partial<ExcelJS.Row> | undefined
  ): void {
    if (title) {
      // 插入标题
      this.worksheet?.spliceRows(1, 0, [title])
      this.worksheet?.mergeCells(1, 1, 1, this.header.length)
      // 调整标题样式
      const titleRow = this.worksheet!.getRow(1)
      // 高度
      titleRow.height = style?.height || 40
      // 字体设置
      titleRow.font = style?.font || {
        size: 20,
        bold: true
      }
      // 背景色
      titleRow.fill = style?.fill || {
        bgColor: {
          argb: 'FFFFFF00'
        },
        type: 'pattern',
        pattern: 'none'
      }
      // 对齐方式
      titleRow.alignment = style?.alignment || {
        horizontal: 'center',
        vertical: 'middle'
      }
    }
  }

  // 添加小标题
  // subTitle 标题
  // style 小标题样式

  private getSubTitle(
    subTitle: string | undefined,
    style?: Partial<ExcelJS.Row> | undefined
  ): void {
    if (subTitle) {
      this.worksheet?.spliceRows(2, 0, [subTitle])
      this.worksheet?.mergeCells(2, 1, 2, this.header.length)
      // 调整标题样式
      const subtitleRow = this.worksheet!.getRow(2)
      // 高度
      subtitleRow.height = style?.height || 20
      // 字体设置
      subtitleRow.font = style?.font || {
        size: 14
      }
      // 背景色
      subtitleRow.fill = style?.fill || {
        bgColor: {
          argb: 'FFFFFF00'
        },
        type: 'pattern',
        pattern: 'none'
      }
      // 对齐方式
      subtitleRow.alignment = style?.alignment || {
        horizontal: 'right',
        vertical: 'middle'
      }
    }
  }

  // 设置表头样式
  // num 表头在第几行
  // data 总数据
  // style 表头样式

  private setHeaderStyle(
    num: number,
    data: any,
    style?: Partial<ExcelJS.Row>
  ): void {
    // 自动筛选器
    this.worksheet!.autoFilter = {
      from: {
        row: num,
        column: 1
      },
      to: {
        row: data.length,
        column: this.header.length
      }
    }
    // 给表头添加背景色
    const headerRow = this.worksheet!.getRow(num)
    headerRow!.height = style?.height || 30
    // 通过 cell 设置背景色，更精准
    headerRow?.eachCell((cell) => {
      cell.fill = style?.fill || {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'dde0e7' }
      }
      cell.font = style?.font || {
        size: 12
      }
    })
  }

  // 其他内容处理
  // password 密码
  // mergeList 合并行列数组
  // headerRowId  表头行序号

  private handleDealExcel(
    password: string | undefined,
    mergeList: mergeListType[] | undefined,
    headerRowId: number
  ) {
    // 添加工作表保护
    if (password) {
      this.worksheet?.protect(password, {
        autoFilter: true,
        selectLockedCells: false
      })
    }
    // 合并单元格
    if (mergeList && mergeList.length > 0) {
      mergeList.forEach((item) => {
        // 行数为表格数据所在行行数+表头行序号headerRowId
        const startRow = item.startRow + headerRowId
        const endRow = item.endRow + headerRowId
        this.worksheet?.mergeCells(
          startRow,
          item.startColumn,
          endRow,
          item.endColumn
        )
      })
    }
    // 冻结前几行
    this.worksheet!.views = [
      { state: 'frozen', xSplit: 0, ySplit: headerRowId }
    ]
  }
}
